import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type { ThreadMessage, TodoItemData } from '@/types';
import {
  extractPlanDataFromMessages,
  hasPlanInMessages,
  type ExtractedPlanData,
} from '@/utils/plan.utils';
import { usePlanModeOptional } from './PlanModeContext';

/**
 * Check if messages indicate streaming state
 */
function isStreamingMessage(
  messages: ThreadMessage[],
  previousMessageCount: number
): boolean {
  const currentMessageCount = messages.length;
  if (currentMessageCount > previousMessageCount) {
    return true;
  }

  return messages.some(
    (msg) =>
      msg.status === 'streaming' ||
      msg.status === 'pending' ||
      (msg.content &&
        Array.isArray(msg.content) &&
        msg.content.some(
          (part: unknown) =>
            typeof part === 'object' &&
            part !== null &&
            'type' in part &&
            part.type === 'tool-call' &&
            'status' in part &&
            (part.status === 'executing' || part.status === 'pending')
        ))
  );
}

/**
 * Check if plan data exists in extracted data, messages, or plan mode context
 */
function hasPlanData(
  extractedData: ExtractedPlanData | null,
  planModeContext: ReturnType<typeof usePlanModeOptional>,
  messages: ThreadMessage[]
): boolean {
  if (!extractedData) {
    return (
      hasPlanInMessages(messages) ||
      planModeContext?.currentPlan != null
    );
  }
  return (
    hasPlanInMessages(messages) ||
    planModeContext?.currentPlan != null ||
    extractedData.planId !== null
  );
}

interface TodoContextType {
  todos: TodoItemData[];
  setTodos: (todos: TodoItemData[]) => void;
  isTodoPanelOpen: boolean;
  setIsTodoPanelOpen: (open: boolean) => void;
  activeTodosCount: number;
  completedCount: number;
  totalCount: number;
  extractTodosFromMessages: (messages: ThreadMessage[]) => void;
  clearTodos: () => void;
}

const TodoContext = createContext<TodoContextType | undefined>(undefined);

interface TodoProviderProps {
  children: ReactNode;
}

export const TodoProvider: React.FC<TodoProviderProps> = ({ children }) => {
  const [todos, setTodos] = useState<TodoItemData[]>([]);
  const [isTodoPanelOpen, setIsTodoPanelOpen] = useState(false);
  const previousTodosRef = useRef<TodoItemData[]>([]);
  const previousMessageCountRef = useRef<number>(0);
  const planModeContext = usePlanModeOptional();

  const activeTodosCount = todos.filter(
    (t) => t.status === 'pending' || t.status === 'in_progress'
  ).length;

  const completedCount = todos.filter(
    (t) => t.status === 'completed'
  ).length;

  const totalCount = todos.length;

  const extractTodosFromMessages = useCallback(
    (messages: ThreadMessage[]) => {
      const extracted = extractPlanDataFromMessages(messages);
      const previousMessageCount = previousMessageCountRef.current;

      // Default to empty data if nothing extracted
      const extractedData: ExtractedPlanData = extracted || {
        todos: [],
        planText: null,
        planId: null,
      };

      // Detect if this is streaming (new messages added) vs history load
      const isStreaming = isStreamingMessage(
        messages,
        previousMessageCount
      );

      const isFirstTimeCreation =
        previousTodosRef.current.length === 0 &&
        extractedData.todos.length > 0;

      // Update plan context if plan data is found (even if planText is null)
      if (planModeContext && extractedData.planId) {
        planModeContext.setCurrentPlan({
          id: extractedData.planId,
          planText: extractedData.planText || '',
          todos: extractedData.todos,
        });
      }

      // Check for plan in messages directly, or fallback to planModeContext
      const hasPlan = hasPlanData(
        extracted,
        planModeContext,
        messages
      );

      setTodos(extractedData.todos);
      previousTodosRef.current = extractedData.todos;
      previousMessageCountRef.current = messages.length;

      // Auto-open TodoSidebar only during streaming when initial plan is created
      // Don't auto-open when loading history
      if (isFirstTimeCreation && hasPlan && isStreaming) {
        setIsTodoPanelOpen(true);
      }
    },
    [planModeContext]
  );

  const clearTodos = useCallback(() => {
    setTodos([]);
    previousTodosRef.current = [];
    previousMessageCountRef.current = 0;
  }, []);

  const value: TodoContextType = {
    todos,
    setTodos,
    isTodoPanelOpen,
    setIsTodoPanelOpen,
    activeTodosCount,
    completedCount,
    totalCount,
    extractTodosFromMessages,
    clearTodos,
  };

  return (
    <TodoContext.Provider value={value}>
      {children}
    </TodoContext.Provider>
  );
};

export const useTodos = (): TodoContextType => {
  const context = useContext(TodoContext);
  if (context === undefined) {
    throw new Error('useTodos must be used within a TodoProvider');
  }
  return context;
};

/**
 * Optional version of useTodos that returns null if not within provider.
 * Useful for components that may or may not have TodoProvider as ancestor.
 */
export const useTodosOptional = (): TodoContextType | null => {
  return useContext(TodoContext) ?? null;
};

